/**
 * Support ticket domain types.
 *
 * This is the SINGLE contract shared by the mock API (Milestone 1) and the
 * real backend API (Milestone 3+). When the backend `Support Ticket` doctype
 * ships, only `api/supportApi.ts` changes — these types and every component
 * stay identical. Keep field names in sync with the doctype fieldnames
 * (camelCase here ⇄ snake_case on the Frappe side).
 */

export type TicketStatus =
  | "Open"
  | "In Progress"
  | "Waiting on Customer"
  | "Resolved"
  | "Closed";

export type TicketPriority = "Low" | "Medium" | "High" | "Urgent";

export type TicketCategory =
  | "Billing"
  | "Technical"
  | "Bug"
  | "Feature Request"
  | "Other";

export const TICKET_STATUSES: TicketStatus[] = [
  "Open",
  "In Progress",
  "Waiting on Customer",
  "Resolved",
  "Closed",
];

export const TICKET_PRIORITIES: TicketPriority[] = [
  "Low",
  "Medium",
  "High",
  "Urgent",
];

export const TICKET_CATEGORIES: TicketCategory[] = [
  "Billing",
  "Technical",
  "Bug",
  "Feature Request",
  "Other",
];

export interface TicketAttachment {
  name: string;
  url: string;
}

export interface TicketMessage {
  id: string;
  /** Who wrote it: the tenant customer, or a Zaviago support agent. */
  senderType: "customer" | "agent";
  senderName: string;
  /** Plain-text form — always present, used for previews/notifications. */
  body: string;
  /**
   * Rich HTML body (bold/lists + inline <img>/<video>). When present it is
   * rendered instead of `body`. Sanitized before render.
   */
  bodyHtml?: string;
  /** ISO 8601 timestamp. */
  sentOn: string;
  attachments?: TicketAttachment[];
  /** Quote-reply: the message this one is replying to. */
  replyTo?: {
    id: string;
    senderType: "customer" | "agent";
    snippet: string;
  };
  /** Client-only optimistic states (never sent by the backend). */
  pending?: boolean;
  failed?: boolean;
}

/** Lightweight shape returned by the list endpoint (no full thread). */
export interface TicketSummary {
  name: string; // e.g. "TKT-2026-00042"
  subject: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  raisedBy: string;
  raisedByName: string;
  assignedAgent?: string;
  createdOn: string;
  lastActivityOn: string;
  lastMessagePreview: string;
  messageCount: number;
  /** True when the latest message is from an agent and unread by the customer. */
  unread?: boolean;
  /** Store/tenant this ticket belongs to — only populated in the agent (john) inbox. */
  tenant?: string;
  tenantName?: string;
  /** Private answering-side status — only populated + editable in the agent inbox. */
  internalStatus?: InternalStatus;
}

/** Agent-only pipeline status (customer never sees this). */
export type InternalStatus =
  | "New"
  | "Investigating"
  | "Fixing"
  | "Draft Ready"
  | "Approved"
  | "Waiting"
  | "Done";

export const INTERNAL_STATUSES: InternalStatus[] = [
  "New",
  "Investigating",
  "Fixing",
  "Draft Ready",
  "Approved",
  "Waiting",
  "Done",
];

/** Full ticket including the conversation thread (detail endpoint). */
export interface Ticket extends Omit<TicketSummary, "lastMessagePreview" | "messageCount" | "unread"> {
  messages: TicketMessage[];
  /** Customer satisfaction rating, set after the ticket is resolved. */
  satisfaction?: "up" | "down";
  /** Auto-captured page/session context from when it was raised. */
  context?: TicketContext;
  /**
   * Agent-only UNSENT draft reply — present ONLY in the john receiving-inbox
   * view (get_incoming_ticket). The customer API never returns it, so a draft is
   * never visible to the tenant customer.
   */
  agentDraft?: AgentDraft | null;
}

/** An unsent agent draft reply (john inbox only). */
export interface AgentDraft {
  body: string;
  bodyHtml?: string | null;
  updatedOn?: string | null;
}

/** A stall (storefront) within a business, for the john-inbox tenant filter. */
export interface TenantStall {
  id: string;
  name: string;
  count: number;
}

/**
 * A business (company) and its stalls, only those with tickets — powers the
 * grouped tenant dropdown in the john receiving inbox.
 */
export interface TenantGroup {
  company: string;
  count: number;
  stalls: TenantStall[];
}

/**
 * Where/who/when the ticket was raised from — auto-captured so support can
 * pull up the exact page + PostHog session later. Attached to the ticket.
 */
export interface TicketContext {
  /** Path + query the user was on when they opened the request. */
  url: string;
  /** Friendly page name (from the nav config), if resolved. */
  pageName?: string;
  /** ISO capture time. */
  capturedAt: string;
  userEmail?: string;
  tenantId?: string;
  company?: string;
  /** PostHog session id → watch the replay later. */
  sessionId?: string;
  /** The specific record the user was viewing (product / order / customer). */
  entity?: {
    type: "product" | "order" | "customer" | "page";
    /** Record id / code (e.g. SHP-28432842619, SO-2026-00042). */
    id: string;
    /** Resolved display name, when available. */
    name?: string;
    /** Thumbnail (product photo etc.); real backend fills this in. */
    imageUrl?: string;
  };
}

export interface CreateTicketInput {
  subject: string;
  category: TicketCategory;
  priority: TicketPriority;
  /** Rich HTML description (may contain inline <img>/<video>). */
  bodyHtml: string;
  /** Plain-text fallback derived from bodyHtml. */
  body: string;
  /** Auto-captured page/session context. */
  context?: TicketContext;
}
